/**
 * Helpers puros del soft limit Crece: métrica más caliente y cupo restante.
 * Nunca bloquea caja/POS — solo aviso → Activar plan Crece.
 */

export type UsageDetail = {
  /** Ratio 0..1+ (se clampea para el meter). */
  ratio: number;
  /** Unidades que quedan antes del tope. */
  remaining: number;
  singular: string;
  plural: string;
  /** `Ventas 2.640 de 3.000 este mes` (detalle secundario). */
  meta: string;
  /** Uso crudo del período. */
  used: number;
  /** Tope del período. */
  limit: number;
};

const METRIC_LABEL: Record<string, { singular: string; plural: string }> = {
  pos_transactions: { singular: 'venta', plural: 'ventas' },
  online_orders: { singular: 'pedido', plural: 'pedidos' },
  appointments: { singular: 'turno', plural: 'turnos' },
};

export function nearLimit(used: number | null | undefined, max: number | null | undefined): boolean {
  if (max == null || max <= 0) return false;
  const u = Math.max(0, used ?? 0);
  return u / max >= 0.8;
}

const CAP_ORDER = ['pos_transactions', 'online_orders', 'appointments'];

const capOrderIndex = (key: string) => {
  const i = CAP_ORDER.indexOf(key);
  return i === -1 ? CAP_ORDER.length : i;
};

export function hottestVolumeMetric(
  metrics: Array<{ key: string; used: number; limit: number; ratio: number }> | undefined,
): UsageDetail | null {
  if (!metrics?.length) return null;
  // Empate de ratio (p. ej. todo en 0): gana la métrica con más uso real y
  // después el orden ventas → pedidos → turnos, no el alfabético del backend.
  const hottest = [...metrics].sort(
    (a, b) => b.ratio - a.ratio || b.used - a.used || capOrderIndex(a.key) - capOrderIndex(b.key),
  )[0];
  if (!hottest || hottest.limit <= 0) return null;
  const label = METRIC_LABEL[hottest.key] ?? {
    singular: hottest.key,
    plural: hottest.key,
  };
  const remaining = Math.max(0, hottest.limit - hottest.used);
  const capLabel = remaining === 1 ? label.singular : label.plural;
  return {
    ratio: hottest.ratio,
    remaining,
    singular: label.singular,
    plural: label.plural,
    meta: `${capLabel.charAt(0).toUpperCase()}${capLabel.slice(1)} ${hottest.used} de ${hottest.limit} este mes`,
    used: hottest.used,
    limit: hottest.limit,
  };
}

/**
 * Línea de uso de volumen para "Mi plan" (BillingCurrentPlanCard):
 * `Ventas este mes` + `726/250`. Misma métrica caliente que el card de Home.
 */
export function volumeUsageLine(
  metrics: Array<{ key: string; used: number; limit: number; ratio: number }> | undefined,
): { label: string; value: string; overQuota: boolean; used: number; limit: number } | null {
  const detail = hottestVolumeMetric(metrics);
  if (!detail) return null;
  const label = `${detail.plural.charAt(0).toUpperCase()}${detail.plural.slice(1)} este mes`;
  return {
    label,
    value: `${detail.used}/${detail.limit}`,
    overQuota: detail.ratio >= 1,
    used: detail.used,
    limit: detail.limit,
  };
}

/**
 * Métricas de volumen vigentes para la org: capability activa o ya con uso.
 * `active` llega desde el RPC (cutover capability gates); backend viejo sin el
 * flag se asume activa.
 */
export function activeVolumeMetrics<T extends { used: number; active?: boolean }>(
  metrics: T[] | undefined,
): T[] {
  return (metrics ?? []).filter((m) => m.active !== false || m.used > 0);
}

/**
 * Topes mensuales activos como frases para copy: `['250 ventas', '25 pedidos']`.
 * Orden fijo ventas → pedidos → turnos; turnos solo si la org tiene agenda.
 */
export function activeVolumeCaps(
  metrics: Array<{ key: string; used: number; limit: number; ratio?: number; active?: boolean }> | undefined,
): string[] {
  const active = activeVolumeMetrics(metrics);
  return CAP_ORDER.map((key) => active.find((m) => m.key === key))
    .filter((m) => m != null && m.limit > 0)
    .map((m) => {
      const label = METRIC_LABEL[m!.key];
      return `${m!.limit} ${m!.limit === 1 ? label?.singular ?? m!.key : label?.plural ?? m!.key}`;
    });
}

export function hottestStructuralUsage(entitlements: {
  branch_count?: number | null;
  max_branches: number | null;
  user_count?: number | null;
  max_users: number | null;
}): UsageDetail | null {
  const candidates: UsageDetail[] = [];
  if (entitlements.max_branches != null && entitlements.max_branches > 0) {
    const used = Math.max(0, entitlements.branch_count ?? 0);
    candidates.push({
      ratio: used / entitlements.max_branches,
      remaining: Math.max(0, entitlements.max_branches - used),
      singular: 'sucursal',
      plural: 'sucursales',
      meta: `${used} de ${entitlements.max_branches} sucursales`,
      used,
      limit: entitlements.max_branches,
    });
  }
  if (entitlements.user_count != null && entitlements.max_users != null && entitlements.max_users > 0) {
    const used = Math.max(0, entitlements.user_count);
    candidates.push({
      ratio: used / entitlements.max_users,
      remaining: Math.max(0, entitlements.max_users - used),
      singular: 'usuario',
      plural: 'usuarios',
      meta: `${used} de ${entitlements.max_users} usuarios`,
      used,
      limit: entitlements.max_users,
    });
  }
  if (candidates.length === 0) return null;
  return candidates.sort((a, b) => b.ratio - a.ratio)[0];
}
